import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  migrateUiPreferenceStorageKeys,
  parseUiPreferences,
  persistUiPreferencesLocal,
  saveUiPreferencesToServer,
} from '../config/uiPreferences';

// Theme system - Only QMS Professional theme active
export const themes = {
  // CUSTOM QMS THEME - Optimized for Quality Management Systems
  qms_professional: {
    name: 'QMS Professional',
    category: 'specialized',
    colors: {
      primary: 'bg-blue-700',
      secondary: 'bg-slate-600',
      accent: 'bg-sky-600',
      success: 'bg-green-600',
      warning: 'bg-amber-500',
      danger: 'bg-red-600',
      info: 'bg-blue-600',
      surface: 'bg-white',
      background: 'bg-slate-50',
      backgroundHover: 'bg-slate-100'
    },
    gradients: {
      header: 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white',
      card: 'bg-white',
      sidebar: 'bg-gradient-to-b from-white to-blue-50',
      background: 'bg-gradient-to-br from-slate-50 to-blue-50',
      subtle: 'bg-gradient-to-r from-blue-50 to-slate-50'
    },
    text: {
      primary: 'text-slate-900',
      secondary: 'text-slate-700',
      muted: 'text-slate-500',
      inverse: 'text-white'
    },
    border: {
      light: 'border-slate-200',
      default: 'border-slate-300',
      dark: 'border-slate-400'
    },
    shadow: {
      sm: 'shadow-sm',
      default: 'shadow-lg shadow-blue-500/10',
      lg: 'shadow-xl shadow-indigo-500/15',
      xl: 'shadow-2xl shadow-blue-600/20'
    },
    // Special QMS-specific properties
    status: {
      approved: 'bg-green-50 text-green-700 border-green-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      rejected: 'bg-rose-50 text-rose-700 border-rose-200',
      draft: 'bg-slate-50 text-slate-700 border-slate-200',
      review: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    priority: {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-600 text-white',
      medium: 'bg-yellow-500 text-white',
      low: 'bg-green-500 text-white'
    },
    sidebar: {
      collapseBtn:
        'bg-white/20 hover:bg-white/35 border border-white/40 text-white shadow-md hover:shadow-lg ring-1 ring-inset ring-white/20 hover:ring-white/30',
      collapseBtnIcon: 'text-white',
    },
  },

  /**
   * QMS Clarity — flat surfaces, minimal gradients, optimized for long sessions and dense tables.
   * Pairs best with table UI "Refined" (Νέο).
   */
  qms_clarity: {
    name: 'QMS Clarity',
    category: 'specialized',
    colors: {
      primary: 'bg-blue-600',
      secondary: 'bg-slate-600',
      accent: 'bg-blue-500',
      success: 'bg-green-600',
      warning: 'bg-amber-500',
      danger: 'bg-red-600',
      info: 'bg-blue-500',
      surface: 'bg-white',
      background: 'bg-slate-50',
      backgroundHover: 'bg-slate-100',
    },
    gradients: {
      header: 'bg-blue-600 text-white border-b border-blue-700',
      card: 'bg-white border border-slate-200',
      sidebar: 'bg-white border-r border-slate-200',
      background: 'bg-slate-50',
      subtle: 'bg-blue-50/40',
    },
    text: {
      primary: 'text-slate-900',
      secondary: 'text-slate-700',
      muted: 'text-slate-500',
      inverse: 'text-white',
    },
    border: {
      light: 'border-slate-200',
      default: 'border-slate-300',
      dark: 'border-slate-400',
    },
    shadow: {
      sm: 'shadow-sm',
      default: 'shadow-md shadow-slate-200/80',
      lg: 'shadow-lg shadow-slate-300/50',
      xl: 'shadow-xl shadow-slate-400/40',
    },
    status: {
      approved: 'bg-green-50 text-green-800 border-green-200',
      pending: 'bg-amber-50 text-amber-800 border-amber-200',
      rejected: 'bg-rose-50 text-rose-800 border-rose-200',
      draft: 'bg-slate-50 text-slate-700 border-slate-200',
      review: 'bg-blue-50 text-blue-700 border-blue-200',
    },
    priority: {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-600 text-white',
      medium: 'bg-amber-500 text-slate-900',
      low: 'bg-green-600 text-white',
    },
    sidebar: {
      selected:
        'bg-blue-50 text-blue-700 border border-blue-100 shadow-sm',
      hover: 'bg-slate-100',
      activeIcon: 'text-blue-600',
      activeDot: 'bg-blue-400',
      drawerSelected: 'bg-blue-50 text-blue-700 border border-blue-100 shadow-md',
      drawerAccent: 'bg-blue-100 text-blue-600',
      collapseBtn:
        'bg-white/20 hover:bg-white/35 border border-white/40 text-white shadow-md hover:shadow-lg ring-1 ring-inset ring-white/20 hover:ring-white/30',
      collapseBtnIcon: 'text-white',
    },
  },

  /** Primary dark theme — pairs with refined table UI and Tailwind `dark` class on `<html>`. */
  qms_dark: {
    name: 'QMS Dark',
    category: 'dark',
    isDark: true,
    colors: {
      primary: 'bg-blue-600',
      secondary: 'bg-slate-600',
      accent: 'bg-sky-500',
      success: 'bg-emerald-600',
      warning: 'bg-amber-500',
      danger: 'bg-red-500',
      info: 'bg-blue-500',
      surface: 'bg-slate-900',
      background: 'bg-slate-950',
      backgroundHover: 'bg-slate-800',
    },
    gradients: {
      header: 'bg-gradient-to-r from-slate-900 via-blue-950 to-indigo-950 text-white border-b border-slate-800',
      card: 'bg-slate-900 border border-slate-800',
      sidebar: 'bg-gradient-to-b from-slate-950 via-slate-900 to-blue-950/30 border-r border-slate-800',
      background: 'bg-slate-950',
      subtle: 'bg-gradient-to-r from-slate-900/90 to-blue-950/50',
    },
    text: {
      primary: 'text-slate-100',
      secondary: 'text-slate-300',
      muted: 'text-slate-500',
      inverse: 'text-white',
    },
    border: {
      light: 'border-slate-800',
      default: 'border-slate-700',
      dark: 'border-slate-600',
    },
    shadow: {
      sm: 'shadow-sm shadow-black/25',
      default: 'shadow-lg shadow-black/35',
      lg: 'shadow-xl shadow-black/45',
      xl: 'shadow-2xl shadow-black/55',
    },
    status: {
      approved: 'bg-emerald-950/60 text-emerald-300 border-emerald-800',
      pending: 'bg-amber-950/60 text-amber-300 border-amber-800',
      rejected: 'bg-rose-950/60 text-rose-300 border-rose-800',
      draft: 'bg-slate-800/80 text-slate-300 border-slate-700',
      review: 'bg-blue-950/60 text-blue-300 border-blue-800',
    },
    priority: {
      critical: 'bg-red-600 text-white',
      high: 'bg-orange-600 text-white',
      medium: 'bg-amber-500 text-slate-900',
      low: 'bg-emerald-600 text-white',
    },
    sidebar: {
      header:
        'bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950/70 border-b border-slate-700/80 text-white',
      logo: 'text-white',
      logoMuted: 'text-slate-400',
      headerIconBox:
        'p-2 rounded-lg bg-white/10 text-white ring-1 ring-inset ring-white/10',
      collapseBtn:
        'bg-white/15 hover:bg-white/25 border border-white/30 text-white shadow-md hover:shadow-lg ring-1 ring-inset ring-white/15 hover:ring-white/25',
      collapseBtnIcon: 'text-white',
      selected:
        'bg-blue-950/50 text-blue-200 border border-blue-800/60 shadow-sm',
      hover: 'hover:bg-slate-800/80',
      activeIcon: 'text-blue-400',
      activeDot: 'bg-blue-400',
      drawerSelected:
        'bg-blue-950/40 text-blue-200 border border-blue-800/50 shadow-md',
      drawerAccent: 'bg-blue-900/50 text-blue-300',
    },
  },

  // CUSTOM THEME - Professional Color Palette
  professional_custom: {
    name: 'Professional Custom',
    category: 'specialized',
    colors: {
      primary: 'bg-[#11224E]',
      secondary: 'bg-[#F87B1B]',
      accent: 'bg-[#CBD99B]',
      success: 'bg-emerald-600',
      warning: 'bg-[#F87B1B]',
      danger: 'bg-red-600',
      info: 'bg-[#11224E]',
      surface: 'bg-[#EEEEEE]',
      background: 'bg-[#EEEEEE]'
    },
    gradients: {
      header: 'bg-gradient-to-r from-[#11224E] to-[#F87B1B] text-white',
      card: 'bg-[#EEEEEE]',
      sidebar: 'bg-gradient-to-b from-[#EEEEEE] to-[#11224E]',
      background: 'bg-gradient-to-br from-[#EEEEEE] to-[#CBD99B]/20',
      subtle: 'bg-gradient-to-r from-[#EEEEEE] via-[#CBD99B]/10 to-[#F87B1B]/10'
    },
    text: {
      primary: 'text-[#11224E]',
      secondary: 'text-[#4A5568]',
      muted: 'text-[#718096]',
      inverse: 'text-white'
    },
    border: {
      light: 'border-[#11224E]/30',
      default: 'border-[#11224E]/50',
      dark: 'border-[#11224E]/70'
    },
    shadow: {
      sm: 'shadow-sm shadow-[#11224E]/10',
      default: 'shadow-lg shadow-[#11224E]/15',
      lg: 'shadow-xl shadow-[#F87B1B]/20',
      xl: 'shadow-2xl shadow-[#CBD99B]/25'
    },
    // Special QMS-specific properties
    status: {
      approved: 'bg-green-50 text-green-700 border-green-200',
      pending: 'bg-[#F87B1B]/10 text-[#F87B1B] border-[#F87B1B]/20',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      draft: 'bg-[#EEEEEE] text-[#11224E] border-[#11224E]/20',
      review: 'bg-[#CBD99B]/20 text-[#11224E] border-[#CBD99B]/30'
    },
    priority: {
      critical: 'bg-red-600 text-white',
      high: 'bg-[#F87B1B] text-white',
      medium: 'bg-[#CBD99B] text-[#11224E]',
      low: 'bg-green-500 text-white'
    }
  },

  // CORPORATE THEME - Blue & Teal Professional Palette
  corporate_blue: {
    name: 'Corporate Blue',
    category: 'specialized',
    colors: {
      primary: 'bg-[#05339C]',
      secondary: 'bg-[#1055C9]',
      accent: 'bg-[#41A67E]',
      success: 'bg-[#41A67E]',
      warning: 'bg-[#E5C95F]',
      danger: 'bg-red-600',
      info: 'bg-[#1055C9]',
      surface: 'bg-white',
      background: 'bg-gradient-to-br from-slate-50 to-blue-50'
    },
    gradients: {
      header: 'bg-gradient-to-r from-[#05339C] to-[#1055C9] text-white',
      card: 'bg-white',
      sidebar: 'bg-gradient-to-b from-white to-[#05339C]',
      background: 'bg-gradient-to-br from-slate-50 to-blue-50',
      subtle: 'bg-gradient-to-r from-white via-blue-50 to-[#41A67E]/10'
    },
    text: {
      primary: 'text-[#05339C]',
      secondary: 'text-[#1055C9]',
      muted: 'text-slate-600',
      inverse: 'text-white'
    },
    border: {
      light: 'border-[#05339C]/20',
      default: 'border-[#05339C]/30',
      dark: 'border-[#05339C]/50'
    },
    shadow: {
      sm: 'shadow-sm shadow-[#05339C]/10',
      default: 'shadow-lg shadow-[#05339C]/15',
      lg: 'shadow-xl shadow-[#1055C9]/20',
      xl: 'shadow-2xl shadow-[#41A67E]/25'
    },
    // Special QMS-specific properties
    status: {
      approved: 'bg-[#41A67E]/10 text-[#41A67E] border-[#41A67E]/20',
      pending: 'bg-[#E5C95F]/10 text-[#E5C95F] border-[#E5C95F]/20',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      draft: 'bg-slate-50 text-slate-700 border-slate-200',
      review: 'bg-[#1055C9]/10 text-[#1055C9] border-[#1055C9]/20'
    },
    priority: {
      critical: 'bg-red-600 text-white',
      high: 'bg-[#05339C] text-white',
      medium: 'bg-[#1055C9] text-white',
      low: 'bg-[#41A67E] text-white'
    }
  },


  /* COMMENTED OUT THEMES - Can be restored if needed
  
  // OCEAN BREEZE THEME (new)
  ocean_breeze: {
    name: 'Ocean Breeze',
    category: 'modern',
    colors: {
      primary: 'bg-gradient-to-r from-sky-600 to-cyan-600',
      secondary: 'bg-sky-700',
      accent: 'bg-teal-600',
      success: 'bg-emerald-600',
      warning: 'bg-amber-500',
      danger: 'bg-rose-600',
      info: 'bg-cyan-500',
      surface: 'bg-white',
      background: 'bg-gradient-to-br from-sky-50 to-cyan-50'
    },
    gradients: {
      header: 'bg-gradient-to-r from-sky-50 to-cyan-50',
      card: 'bg-gradient-to-br from-white to-sky-50',
      sidebar: 'bg-gradient-to-b from-white to-cyan-50',
      background: 'bg-gradient-to-br from-sky-50 to-cyan-50',
      subtle: 'bg-gradient-to-r from-sky-50 via-cyan-50 to-teal-50'
    },
    text: {
      primary: 'text-slate-900',
      secondary: 'text-slate-600',
      muted: 'text-slate-500',
      inverse: 'text-white'
    },
    border: {
      light: 'border-sky-200',
      default: 'border-sky-300',
      dark: 'border-sky-400'
    },
    shadow: {
      sm: 'shadow-sm',
      default: 'shadow-lg shadow-sky-500/10',
      lg: 'shadow-xl shadow-cyan-500/20',
      xl: 'shadow-2xl shadow-teal-500/20'
    }
  },

  // Additional curated themes
  sunset_blush: {
    name: 'Sunset Blush',
    category: 'modern',
    colors: {
      primary: 'bg-gradient-to-r from-orange-500 to-rose-500',
      secondary: 'bg-orange-600',
      accent: 'bg-rose-600',
      success: 'bg-emerald-600',
      warning: 'bg-amber-500',
      danger: 'bg-red-600',
      info: 'bg-sky-500',
      surface: 'bg-white',
      background: 'bg-gradient-to-br from-orange-50 to-rose-50'
    },
    gradients: {
      header: 'bg-gradient-to-r from-orange-50 to-rose-50',
      card: 'bg-gradient-to-br from-white to-rose-50',
      sidebar: 'bg-gradient-to-b from-white to-orange-50',
      background: 'bg-gradient-to-br from-orange-50 to-rose-50',
      subtle: 'bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50'
    },
    text: { primary: 'text-slate-900', secondary: 'text-slate-600', muted: 'text-slate-500', inverse: 'text-white' },
    border: { light: 'border-rose-200', default: 'border-rose-300', dark: 'border-rose-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-rose-300/20', lg: 'shadow-xl shadow-amber-300/20', xl: 'shadow-2xl shadow-orange-300/25' }
  },
  forest_mist: {
    name: 'Forest Mist',
    category: 'minimal',
    colors: {
      primary: 'bg-emerald-700',
      secondary: 'bg-emerald-600',
      accent: 'bg-teal-600',
      success: 'bg-emerald-600',
      warning: 'bg-yellow-500',
      danger: 'bg-red-600',
      info: 'bg-cyan-500',
      surface: 'bg-white',
      background: 'bg-emerald-50'
    },
    gradients: { header: 'bg-emerald-50', card: 'bg-white', sidebar: 'bg-emerald-50', background: 'bg-emerald-50', subtle: 'bg-gradient-to-r from-emerald-50 to-teal-50' },
    text: { primary: 'text-emerald-950', secondary: 'text-emerald-700', muted: 'text-emerald-600', inverse: 'text-white' },
    border: { light: 'border-emerald-200', default: 'border-emerald-300', dark: 'border-emerald-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-xl' }
  },
  midnight_blue: {
    name: 'Midnight Blue',
    category: 'dark',
    isDark: true,
    colors: {
      primary: 'bg-indigo-700',
      secondary: 'bg-slate-700',
      accent: 'bg-violet-600',
      success: 'bg-emerald-600',
      warning: 'bg-amber-500',
      danger: 'bg-rose-600',
      info: 'bg-sky-500',
      surface: 'bg-slate-900',
      background: 'bg-slate-950'
    },
    gradients: { header: 'bg-slate-900 border-b border-slate-800', card: 'bg-slate-900 border border-slate-800', sidebar: 'bg-slate-900 border-r border-slate-800', background: 'bg-slate-950', subtle: 'bg-gradient-to-r from-slate-900 to-indigo-900' },
    text: { primary: 'text-slate-100', secondary: 'text-slate-300', muted: 'text-slate-500', inverse: 'text-white' },
    sidebar: {
      header: 'bg-slate-900 border-b border-slate-800 text-white',
      logo: 'text-white',
      logoMuted: 'text-slate-400',
      headerIconBox: 'p-2 rounded-lg bg-white/10 text-white ring-1 ring-inset ring-white/10',
      collapseBtn:
        'bg-white/15 hover:bg-white/25 border border-white/30 text-white shadow-md hover:shadow-lg ring-1 ring-inset ring-white/15',
      collapseBtnIcon: 'text-white',
    },
    border: { light: 'border-slate-800', default: 'border-slate-700', dark: 'border-slate-600' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-black/25', lg: 'shadow-xl shadow-black/35', xl: 'shadow-2xl shadow-black/45' }
  },
  desert_sand: {
    name: 'Desert Sand',
    category: 'minimal',
    colors: { primary: 'bg-amber-700', secondary: 'bg-amber-600', accent: 'bg-orange-600', success: 'bg-green-600', warning: 'bg-amber-500', danger: 'bg-red-600', info: 'bg-yellow-500', surface: 'bg-amber-50', background: 'bg-amber-25' },
    gradients: { header: 'bg-amber-50', card: 'bg-amber-50', sidebar: 'bg-amber-50', background: 'bg-amber-25', subtle: 'bg-gradient-to-r from-amber-50 to-orange-50' },
    text: { primary: 'text-amber-950', secondary: 'text-amber-700', muted: 'text-amber-600', inverse: 'text-white' },
    border: { light: 'border-amber-200', default: 'border-amber-300', dark: 'border-amber-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-xl' }
  },
  arctic_ice: {
    name: 'Arctic Ice',
    category: 'modern',
    colors: { primary: 'bg-gradient-to-r from-cyan-500 to-sky-600', secondary: 'bg-cyan-700', accent: 'bg-sky-600', success: 'bg-emerald-600', warning: 'bg-amber-500', danger: 'bg-rose-600', info: 'bg-cyan-500', surface: 'bg-white', background: 'bg-gradient-to-br from-cyan-50 to-sky-50' },
    gradients: { header: 'bg-gradient-to-r from-cyan-50 to-sky-50', card: 'bg-gradient-to-br from-white to-cyan-50', sidebar: 'bg-gradient-to-b from-white to-sky-50', background: 'bg-gradient-to-br from-cyan-50 to-sky-50', subtle: 'bg-gradient-to-r from-cyan-50 via-sky-50 to-white' },
    text: { primary: 'text-slate-900', secondary: 'text-slate-600', muted: 'text-slate-500', inverse: 'text-white' },
    border: { light: 'border-cyan-200', default: 'border-cyan-300', dark: 'border-cyan-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-cyan-500/10', lg: 'shadow-xl shadow-sky-500/15', xl: 'shadow-2xl shadow-cyan-500/20' }
  },
  cherry_blossom: {
    name: 'Cherry Blossom',
    category: 'modern',
    colors: { primary: 'bg-rose-600', secondary: 'bg-pink-600', accent: 'bg-fuchsia-600', success: 'bg-emerald-600', warning: 'bg-amber-500', danger: 'bg-red-600', info: 'bg-pink-500', surface: 'bg-white', background: 'bg-rose-50' },
    gradients: { header: 'bg-gradient-to-r from-rose-50 to-pink-50', card: 'bg-white', sidebar: 'bg-gradient-to-b from-white to-rose-50', background: 'bg-gradient-to-br from-rose-50 to-pink-50', subtle: 'bg-gradient-to-r from-rose-50 to-fuchsia-50' },
    text: { primary: 'text-rose-950', secondary: 'text-rose-700', muted: 'text-rose-600', inverse: 'text-white' },
    border: { light: 'border-rose-200', default: 'border-rose-300', dark: 'border-rose-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-xl' }
  },
  graphite: {
    name: 'Graphite',
    category: 'minimal',
    colors: { primary: 'bg-neutral-900', secondary: 'bg-neutral-700', accent: 'bg-gray-600', success: 'bg-green-600', warning: 'bg-yellow-500', danger: 'bg-red-600', info: 'bg-blue-500', surface: 'bg-white', background: 'bg-neutral-50' },
    gradients: { header: 'bg-white', card: 'bg-white', sidebar: 'bg-white', background: 'bg-neutral-50', subtle: 'bg-gray-50' },
    text: { primary: 'text-neutral-900', secondary: 'text-neutral-600', muted: 'text-neutral-500', inverse: 'text-white' },
    border: { light: 'border-neutral-200', default: 'border-neutral-300', dark: 'border-neutral-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-xl' }
  },
  cyber_teal: {
    name: 'Cyber Teal',
    category: 'modern',
    colors: { primary: 'bg-teal-600', secondary: 'bg-cyan-700', accent: 'bg-fuchsia-600', success: 'bg-emerald-600', warning: 'bg-amber-500', danger: 'bg-rose-600', info: 'bg-cyan-500', surface: 'bg-white', background: 'bg-gradient-to-br from-slate-50 to-teal-50' },
    gradients: { header: 'bg-gradient-to-r from-slate-50 to-teal-50', card: 'bg-white', sidebar: 'bg-gradient-to-b from-white to-teal-50', background: 'bg-gradient-to-br from-slate-50 to-teal-50', subtle: 'bg-gradient-to-r from-cyan-50 to-teal-50' },
    text: { primary: 'text-slate-900', secondary: 'text-slate-600', muted: 'text-slate-500', inverse: 'text-white' },
    border: { light: 'border-teal-200', default: 'border-teal-300', dark: 'border-teal-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-teal-500/10', lg: 'shadow-xl shadow-cyan-500/15', xl: 'shadow-2xl shadow-fuchsia-500/20' }
  },
  sage_garden: {
    name: 'Sage Garden',
    category: 'minimal',
    colors: { primary: 'bg-lime-700', secondary: 'bg-emerald-700', accent: 'bg-green-600', success: 'bg-green-600', warning: 'bg-yellow-500', danger: 'bg-red-600', info: 'bg-teal-500', surface: 'bg-lime-50', background: 'bg-lime-25' },
    gradients: { header: 'bg-lime-50', card: 'bg-lime-50', sidebar: 'bg-lime-50', background: 'bg-lime-25', subtle: 'bg-gradient-to-r from-lime-50 to-emerald-50' },
    text: { primary: 'text-lime-950', secondary: 'text-emerald-700', muted: 'text-emerald-600', inverse: 'text-white' },
    border: { light: 'border-lime-200', default: 'border-lime-300', dark: 'border-lime-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-md', lg: 'shadow-lg', xl: 'shadow-xl' }
  },
  aurora_borealis: {
    name: 'Aurora Borealis',
    category: 'modern',
    colors: { primary: 'bg-gradient-to-r from-emerald-500 to-violet-600', secondary: 'bg-slate-800', accent: 'bg-cyan-600', success: 'bg-emerald-600', warning: 'bg-amber-500', danger: 'bg-rose-600', info: 'bg-sky-500', surface: 'bg-white', background: 'bg-gradient-to-br from-emerald-50 to-violet-50' },
    gradients: { header: 'bg-gradient-to-r from-emerald-50 to-violet-50', card: 'bg-gradient-to-br from-white to-emerald-50', sidebar: 'bg-gradient-to-b from-white to-violet-50', background: 'bg-gradient-to-br from-emerald-50 to-violet-50', subtle: 'bg-gradient-to-r from-emerald-50 via-cyan-50 to-violet-50' },
    text: { primary: 'text-slate-900', secondary: 'text-slate-600', muted: 'text-slate-500', inverse: 'text-white' },
    border: { light: 'border-emerald-200', default: 'border-violet-200', dark: 'border-violet-300' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-emerald-500/10', lg: 'shadow-xl shadow-violet-500/15', xl: 'shadow-2xl shadow-cyan-500/20' }
  },

  // SPECIALIZED OS-INSPIRED THEMES
  windows_xp_bliss: {
    name: 'Windows XP Bliss',
    category: 'specialized',
    colors: {
      primary: 'bg-blue-600',
      secondary: 'bg-green-600',
      accent: 'bg-amber-500',
      success: 'bg-green-600',
      warning: 'bg-yellow-500',
      danger: 'bg-red-600',
      info: 'bg-sky-500',
      surface: 'bg-white',
      background: 'bg-gradient-to-br from-sky-100 to-green-100'
    },
    gradients: {
      header: 'bg-gradient-to-r from-sky-200 to-blue-300',
      card: 'bg-white',
      sidebar: 'bg-gradient-to-b from-white to-green-50',
      background: 'bg-gradient-to-br from-sky-100 to-green-100',
      subtle: 'bg-gradient-to-r from-sky-50 via-green-50 to-white'
    },
    text: { primary: 'text-slate-900', secondary: 'text-slate-700', muted: 'text-slate-500', inverse: 'text-white' },
    border: { light: 'border-blue-200', default: 'border-blue-300', dark: 'border-blue-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-blue-500/10', lg: 'shadow-xl shadow-green-500/10', xl: 'shadow-2xl shadow-sky-500/15' }
  },
  windows_11_mica: {
    name: 'Windows 11 Mica',
    category: 'specialized',
    colors: {
      primary: 'bg-sky-700',
      secondary: 'bg-indigo-600',
      accent: 'bg-blue-500',
      success: 'bg-emerald-600',
      warning: 'bg-amber-500',
      danger: 'bg-rose-600',
      info: 'bg-sky-500',
      surface: 'bg-white',
      background: 'bg-slate-50'
    },
    gradients: {
      header: 'bg-white/80 backdrop-blur-xl border-b border-slate-200',
      card: 'bg-white/80 backdrop-blur-lg border border-slate-200',
      sidebar: 'bg-white/80 backdrop-blur-xl border-r border-slate-200',
      background: 'bg-gradient-to-br from-slate-50 to-sky-50',
      subtle: 'bg-gradient-to-r from-white to-slate-50'
    },
    text: { primary: 'text-slate-900', secondary: 'text-slate-600', muted: 'text-slate-500', inverse: 'text-white' },
    border: { light: 'border-slate-200', default: 'border-slate-300', dark: 'border-slate-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-sky-500/10', lg: 'shadow-xl shadow-indigo-500/15', xl: 'shadow-2xl shadow-sky-600/20' }
  },
  macos_aqua: {
    name: 'macOS Aqua',
    category: 'specialized',
    colors: {
      primary: 'bg-sky-600',
      secondary: 'bg-slate-700',
      accent: 'bg-rose-500',
      success: 'bg-emerald-600',
      warning: 'bg-amber-500',
      danger: 'bg-red-600',
      info: 'bg-cyan-500',
      surface: 'bg-white',
      background: 'bg-gradient-to-br from-slate-50 to-sky-50'
    },
    gradients: {
      header: 'bg-white/80 backdrop-blur-xl border-b border-slate-200',
      card: 'bg-white/90 backdrop-blur-md',
      sidebar: 'bg-white/70 backdrop-blur-lg',
      background: 'bg-gradient-to-br from-slate-50 to-sky-50',
      subtle: 'bg-gradient-to-r from-white to-sky-50'
    },
    text: { primary: 'text-slate-900', secondary: 'text-slate-600', muted: 'text-slate-500', inverse: 'text-white' },
    border: { light: 'border-slate-200', default: 'border-slate-300', dark: 'border-slate-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-sky-500/10', lg: 'shadow-xl shadow-slate-500/15', xl: 'shadow-2xl shadow-sky-600/20' }
  },
  ubuntu_tango: {
    name: 'Ubuntu Tango',
    category: 'specialized',
    colors: {
      primary: 'bg-orange-600',
      secondary: 'bg-purple-700',
      accent: 'bg-amber-500',
      success: 'bg-green-600',
      warning: 'bg-yellow-500',
      danger: 'bg-red-600',
      info: 'bg-orange-500',
      surface: 'bg-white',
      background: 'bg-gradient-to-br from-orange-50 to-amber-50'
    },
    gradients: {
      header: 'bg-gradient-to-r from-orange-500 to-purple-700 text-white',
      card: 'bg-white',
      sidebar: 'bg-gradient-to-b from-white to-orange-50',
      background: 'bg-gradient-to-br from-orange-50 to-amber-50',
      subtle: 'bg-gradient-to-r from-amber-50 to-orange-50'
    },
    text: { primary: 'text-stone-900', secondary: 'text-stone-700', muted: 'text-stone-500', inverse: 'text-white' },
    border: { light: 'border-orange-200', default: 'border-orange-300', dark: 'border-orange-400' },
    shadow: { sm: 'shadow-sm', default: 'shadow-lg shadow-orange-500/10', lg: 'shadow-xl shadow-purple-500/15', xl: 'shadow-2xl shadow-amber-500/20' }
  },
  retro_terminal: {
    name: 'Retro Terminal',
    category: 'specialized',
    colors: {
      primary: 'bg-black',
      secondary: 'bg-zinc-800',
      accent: 'bg-emerald-500',
      success: 'bg-emerald-600',
      warning: 'bg-yellow-500',
      danger: 'bg-red-600',
      info: 'bg-cyan-500',
      surface: 'bg-zinc-900',
      background: 'bg-black'
    },
    gradients: {
      header: 'bg-black border-b border-emerald-700',
      card: 'bg-zinc-900 border border-emerald-800',
      sidebar: 'bg-black border-r border-emerald-800',
      background: 'bg-black',
      subtle: 'bg-gradient-to-r from-black to-zinc-900'
    },
    text: { primary: 'text-emerald-400', secondary: 'text-emerald-500', muted: 'text-emerald-700', inverse: 'text-black' },
    border: { light: 'border-emerald-700', default: 'border-emerald-700', dark: 'border-emerald-600' },
    shadow: { sm: 'shadow-none', default: 'shadow-none', lg: 'shadow-none', xl: 'shadow-none' }
  },


  // SEA BREEZE THEME - Teal/Sea-Green Palette


  /* COMMENTED OUT THEMES - Can be restored if needed
  
  // OCEAN BREEZE THEME (new)
  ocean_breeze: {
    name: 'Ocean Breeze',
    category: 'modern',
    colors: {
      primary: 'bg-gradient-to-r from-sky-600 to-cyan-600',
      secondary: 'bg-sky-700',
      accent: 'bg-teal-600',
      success: 'bg-emerald-600',
      warning: 'bg-amber-500',
      danger: 'bg-rose-600',
      info: 'bg-cyan-500',
      surface: 'bg-white',
      background: 'bg-gradient-to-br from-sky-50 to-cyan-50'
    },
    gradients: {
      header: 'bg-gradient-to-r from-sky-50 to-cyan-50',
      card: 'bg-gradient-to-br from-white to-sky-50',
      sidebar: 'bg-gradient-to-b from-white to-cyan-50',
      background: 'bg-gradient-to-br from-sky-50 to-cyan-50',
      subtle: 'bg-gradient-to-r from-sky-50 via-cyan-50 to-teal-50'
    },
    text: {
      primary: 'text-slate-900',
      secondary: 'text-slate-600',
      muted: 'text-slate-500',
      inverse: 'text-white'
    },
    border: {
      light: 'border-sky-200',
      default: 'border-sky-300',
      dark: 'border-sky-400'
    },
    shadow: {
      sm: 'shadow-sm',
      default: 'shadow-lg shadow-sky-500/10',
      lg: 'shadow-xl shadow-cyan-500/20',
      xl: 'shadow-2xl shadow-teal-500/20'
    }
  }
  */
};

/** Whether a theme key should enable global dark mode (Tailwind `dark` on `<html>`). */
export function isDarkThemeKey(themeKey) {
  const t = themes[themeKey];
  return Boolean(t && (t.isDark === true || t.category === 'dark'));
}

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    migrateUiPreferenceStorageKeys();
    let userPrefs = null;
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) userPrefs = JSON.parse(savedUser)?.uiPreferences;
    } catch {
      /* ignore */
    }
    const { colorTheme } = parseUiPreferences(userPrefs);
    return themes[colorTheme] ? colorTheme : 'qms_professional';
  });

  useEffect(() => {
    const applyFromStorage = () => {
      migrateUiPreferenceStorageKeys();
      let userPrefs = null;
      try {
        const savedUser = localStorage.getItem('user');
        if (savedUser) userPrefs = JSON.parse(savedUser)?.uiPreferences;
      } catch {
        /* ignore */
      }
      const { colorTheme } = parseUiPreferences(userPrefs);
      if (themes[colorTheme]) setCurrentTheme(colorTheme);
    };
    window.addEventListener('ui-preferences-applied', applyFromStorage);
    return () => window.removeEventListener('ui-preferences-applied', applyFromStorage);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (isDarkThemeKey(currentTheme)) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [currentTheme]);

  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      persistUiPreferencesLocal({ colorTheme: themeName });
      saveUiPreferencesToServer({ colorTheme: themeName });
    }
  };

  const theme = themes[currentTheme];

  const value = {
    currentTheme,
    theme,
    themes,
    changeTheme,
    isDark: isDarkThemeKey(currentTheme),
    // Helper functions for clean, modern theming
    getPrimaryClass: () => `${theme.colors.primary} ${theme.text.inverse}`,
    getSecondaryClass: () => `${theme.colors.secondary} ${theme.text.inverse}`,
    getAccentClass: () => `${theme.colors.accent} ${theme.text.inverse}`,
    getSurfaceClass: () => `${theme.colors.surface} ${theme.text.primary}`,
    getBackgroundClass: () => `${theme.colors.background} ${theme.text.primary}`,
    getBorderClass: (weight = 'default') => theme.border[weight],
    getShadowClass: (size = 'default') => theme.shadow[size],
    getTextClass: (variant = 'primary') => theme.text[variant]
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};